/**
 * Cookie Consent Service
 * Manages cookie consent state and provides reactive updates across components
 */

import { ref, computed, readonly } from 'vue';
import { getCookieValue } from './handleCookies';

// Constants
const COOKIE_CONSENT_KEY = 'sp_cookie_consent';

export enum ConsentStatus {
  ACCEPT = 'accept',
  DECLINE = 'decline',
  NOT_SET = 'not-set'
}

// Reactive state for cookie consent
const cookieConsentValue = ref<string | null>(null);

/**
 * Initialize cookie consent from cookies only
 */
const initializeCookieConsent = (): void => {
  cookieConsentValue.value = getCookieValue(COOKIE_CONSENT_KEY);
};

/**
 * Get current cookie consent value
 */
const getCookieConsent = computed<string>(() => {
  return cookieConsentValue.value ?? ConsentStatus.NOT_SET;
});

/**
 * Update cookie consent value from cookies only
 */
const updateCookieConsent = (): void => {
  cookieConsentValue.value = getCookieValue(COOKIE_CONSENT_KEY);
};

/**
 * Check if user has accepted cookies
 */
const hasAcceptedCookies = computed<boolean>(() => {
  return cookieConsentValue.value === ConsentStatus.ACCEPT;
});

/**
 * Check if user has declined cookies
 */
const hasDeclinedCookies = computed<boolean>(() => {
  return cookieConsentValue.value === ConsentStatus.DECLINE;
});

/**
 * Composable hook for cookie consent management
 */
export const useCookieConsent = () => {
  // Initialize on first use
  if (cookieConsentValue.value === null) {
    initializeCookieConsent();
  }

  return {
    // State (readonly to prevent direct mutations)
    cookieConsent: readonly(getCookieConsent),
    hasAcceptedCookies: readonly(hasAcceptedCookies),
    hasDeclinedCookies: readonly(hasDeclinedCookies),

    // Methods
    updateCookieConsent,

    // Constants
    ConsentStatus,
    COOKIE_CONSENT_KEY
  };
};
